import {
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  Link,
  Stack,
  Typography
} from '@mui/material';
import moment from 'moment';
import { useMemo } from 'react';
import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useTranslate } from 'src/locales';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import { MeetingPlatformIcon } from 'src/sections/meetings/components/meeting-platform-icon';
import {
  IMeeting,
  MeetingPlatform,
  MeetingStatus,
  MeetingType
} from 'src/services/meetings/meetings.types';

type MeetingInfoSectionProps = {
  meeting?: IMeeting;
};

function MeetingInfoSection({ meeting }: MeetingInfoSectionProps) {

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const formattedDate = useMemo(() => {
    if (!meeting?.start_at) return '';

    const timezone = meeting.timezone || moment.tz.guess();
    const startMoment = moment.utc(meeting.start_at).tz(timezone);

    return startMoment.format('MMMM D, YYYY');
  }, [meeting?.start_at, meeting?.timezone]);

  const formattedTime = useMemo(() => {
    if (!meeting?.start_at) return '';

    const timezone = meeting.timezone || moment.tz.guess();
    const startMoment = moment.utc(meeting.start_at).tz(timezone);

    return startMoment.format('h:mm A');
  }, [meeting?.start_at, meeting?.timezone]);

  const formattedDuration = useMemo(() => {
    if (!meeting?.duration_minutes) return '';

    const hours = Math.floor(meeting.duration_minutes / 60);
    const minutes = meeting.duration_minutes % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } if (hours > 0) {
      return `${hours}h`;
    }
    return `${minutes}m`;

  }, [meeting?.duration_minutes]);

  if (!meeting) {
    return (
      <Card>
        <CardHeader title={t('meetings.detail.title')} />
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Loading meeting details...
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={t('meetings.detail.title')} />
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Common meeting details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('meetings.detail.description')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {meeting.description || '-'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('meetings.detail.date')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formattedDate}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('meetings.detail.time')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formattedTime} ({meeting.timezone})
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('meetings.detail.duration')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {
                meeting.type === MeetingType.HOSTED || meeting.status === MeetingStatus.COMPLETED ?
                  formattedDuration
                  :
                  '--'
              }
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('meetings.editor.form.related_company.label')}
            </Typography>
            {
              meeting.related_company
                ? <Link
                  href={paths.dashboard.companies.detail(meeting.related_company.id, tenantType, meeting.related_company.company_type)}
                  component={RouterLink}
                >
                  <TruncatedTextField
                    text={meeting.related_company.company_name}
                    mode="characters"
                    limit={25}
                    showMoreEnabled={false}
                    variant="body2"
                    fontWeight="bold"
                    gutterBottom
                    style={{ textTransform: 'capitalize', color: 'text.primary' }}
                  />
                </Link>
                :
                '--'
            }
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('meetings.editor.form.related_candidate.label')}
            </Typography>
            {
              meeting.related_candidate && meeting.related_candidate.candidate_profile
                ? (
                  <Link
                    href={paths.dashboard.candidates.detail(meeting.related_candidate.id)}
                    component={RouterLink}
                  >
                    <TruncatedTextField
                      text={`${meeting.related_candidate.candidate_profile.first_name} ${meeting.related_candidate.candidate_profile.last_name || ''}`}
                      mode="characters"
                      limit={25}
                      showMoreEnabled={false}
                      variant="body2"
                      fontWeight="bold"
                      gutterBottom
                      style={{ textTransform: 'capitalize', color: 'text.primary' }}
                    />
                  </Link>
                )
                : '--'
            }
          </Grid>

          {/* Meeting type specific details */}
          {meeting.type === MeetingType.EXTERNAL_BOT && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('meetings.detail.platform')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <MeetingPlatformIcon platform={meeting.platform} withLabel />
              </Stack>
              <Box sx={{ mt: 1 }}>
                <Link href={meeting.url} target="_blank" rel="noopener" underline="hover">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2">{meeting.url}</Typography>
                    <Iconify icon="eva:external-link-fill" width={16} height={16} />
                  </Stack>
                </Link>
              </Box>
            </Grid>
          )}

          {/* Participants section */}
          {meeting.participants && meeting.participants.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {t('meetings.detail.participants')}
                </Typography>
                <Grid container spacing={2}>
                  {meeting.participants.map((participant, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">
                            {`${participant.first_name} ${participant.last_name || ''}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {participant.email}
                          </Typography>
                        </Stack>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </>
          )}

          {meeting.type === MeetingType.HOSTED && meeting.allow_anonymous !== undefined && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('meetings.editor.form.allow_anonymous.label')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {meeting.allow_anonymous ? 'Yes' : 'No'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Card>
  );
}

export default MeetingInfoSection;